import {Bit, BitType} from '../bit/bit';

export type SnakeDir = 'l'|'r'|'t'|'b';
const map2str = (v) => v > 0 ? v > 1 ? '-' : '+' : v < 0 ? v  < -1 ? '+' : '-' : 0;

export class Snake {
  bits: Bit[] = [];
  private head: Bit;
  direction: SnakeDir = 'r';
  grow = 1;
  colorScheme = 0;
  score = 0;
  ate = 0;
  lives = 5;

  mX = 0;
  mY = 0;
  skipMove = false;
  tooltip?: string;

  set x(x: number) {
    this.mX = x;
    this.head.x = x;
  }
  get x(): number { return this.mX; }

  set y(y: number) {
    this.mY = y;
    this.head.y = y;
  }

  get y(): number { return this.mY; }

  constructor(x = 0, y = 0, dir: SnakeDir) {
    this.head = new Bit(x, y);
    this.head.tooltip = this.tooltip;
    this.bits.push(this.head);
    this.x = x;
    this.y = y;
    this.direction = dir;
    this.move();
    this.fixTailType();
  }

  fixHeadType(): void {
    const types = {l: 'w', r: 'e', t: 'n', b: 's'};
    this.head.type = types[this.direction] as BitType;
  }

  fixNeckType(): void {
    const head = this.head;
    const neck = this.bits[0];
    if (this.bits.length > 1) {
      const next = this.bits[1];

      const dir1 = [neck.x - head.x, neck.y - head.y].map(map2str).join('');
      const dir2 = [next.x - neck.x, next.y - neck.y].map(map2str).join('');
      const d12 = dir1 + dir2;
      const types = {
        '+0-0': 'lr', '-0+0': 'lr',
        '+0+0': 'lr', '-0-0': 'lr',
        '0+0+': 'bu', '0-0-': 'bu',
        '0+0-': 'bu', '0-0+': 'bu',
        '+00+': 'ne', '0--0': 'ne',
        '-00-': 'sw', '0++0': 'sw',
        '+00-': 'se', '0+-0': 'se',
        '-00+': 'nw', '0-+0': 'nw',
      };
      neck.type = types[d12];
      neck.d12 = d12;
      if (!neck.type) {
        console.log(`unhandled neck type`, d12);
      }
    }
  }

  fixTailType(): void {
    const tail = this.bits[this.bits.length - 1];
    const next = this.bits[this.bits.length - 2];
    if (!next) { return; }
    const dir = [next.x - tail.x, next.y - tail.y].map(map2str).join('');
    const types = {
      '+0': 'w',
      '-0': 'e',
      '0-': 's',
      '0+': 'n'
    };
    tail.type = types[dir];
    tail.d12 = dir;
    if (!tail.type) {
      console.log(`unhandled tail type`, dir);
    }
  }

  get vector(): [number, number] {
    const dx = this.direction === 'l' ? -1 : this.direction === 'r' ? 1 : 0;
    const dy = this.direction === 't' ? -1 : this.direction === 'b' ? 1 : 0;
    return [dx, dy];
  }

  preMove(): boolean {
    const [dx, dy] = this.vector;
    this.head = new Bit(this.x, this.y);
    this.x += dx;
    this.y += dy;
    this.fixHeadType();
    this.fixNeckType();
    let bite = false;
    for (const bit of this.bits) {
      if (bit !== this.head && bit.x === this.mX && bit.y === this.mY) {
        bit.type = 'bite';
        bite = true;
      }
    }
    return !bite;
  }

  get length(): number { return this.bits.length; }

  move(complete = true): boolean {
    const result = complete ? this.preMove() : true;
    this.bits.splice(0, 0, this.head);
    const grow = this.grow;
    if (grow > 0) {
      this.grow --;
    } else if (grow < 0) {
      this.grow ++;
      if (this.length > 3) {
        this.bits.splice(this.bits.length - 1, 1);
      }
    }
    if (grow <= 0 && this.length > 2) {
        this.bits.splice(this.bits.length - 1, 1);
        this.fixTailType();
    }
    return result;
  }

  reverse(): void {
    let dir = this.direction;
    switch (this.direction) {
      case 'l': dir = 'r'; break;
      case 'r': dir = 'l'; break;
      case 'b': dir = 't'; break;
      case 't': dir = 'b'; break;
    }
    this.direction = dir;
    this.fixHeadType();
  }
}
